import React, { createContext, useContext, useReducer, useEffect, useCallback, useMemo } from 'react';
import api, { utils } from '../services/api';

const AppContext = createContext();

const initialState = {
    facilities: [],
    facilitiesLoading: false,
    notifications: [],
    systemStatus: null,
    analysisTypes: [],
    runningAnalyses: {},
    runningScraping: [],
    queuedScraping: [],
    scrapingProgress: {},
    apiAvailable: false,
};

const ActionTypes = {
    SET_FACILITIES: 'SET_FACILITIES',
    SET_FACILITIES_LOADING: 'SET_FACILITIES_LOADING',
    ADD_NOTIFICATION: 'ADD_NOTIFICATION',
    REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
    SET_SYSTEM_STATUS: 'SET_SYSTEM_STATUS',
    SET_ANALYSIS_TYPES: 'SET_ANALYSIS_TYPES',
    SET_RUNNING_ANALYSES: 'SET_RUNNING_ANALYSES',
    SET_RUNNING_SCRAPING: 'SET_RUNNING_SCRAPING',
    SET_QUEUED_SCRAPING: 'SET_QUEUED_SCRAPING',
    SET_SCRAPING_PROGRESS: 'SET_SCRAPING_PROGRESS',
    SET_API_AVAILABLE: 'SET_API_AVAILABLE',
};

const appReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.SET_FACILITIES:
            return { ...state, facilities: action.payload, facilitiesLoading: false };
        case ActionTypes.SET_FACILITIES_LOADING:
            return { ...state, facilitiesLoading: action.payload };
        case ActionTypes.ADD_NOTIFICATION:
            return { ...state, notifications: [...state.notifications, { ...action.payload, id: Date.now() }] };
        case ActionTypes.REMOVE_NOTIFICATION:
            return { ...state, notifications: state.notifications.filter(n => n.id !== action.payload) };
        case ActionTypes.SET_SYSTEM_STATUS:
            return { ...state, systemStatus: action.payload };
        case ActionTypes.SET_ANALYSIS_TYPES:
            return { ...state, analysisTypes: action.payload };
        case ActionTypes.SET_RUNNING_ANALYSES:
            return { ...state, runningAnalyses: action.payload };
        case ActionTypes.SET_RUNNING_SCRAPING:
            return { ...state, runningScraping: action.payload };
        case ActionTypes.SET_QUEUED_SCRAPING:
            return { ...state, queuedScraping: action.payload };
        case ActionTypes.SET_SCRAPING_PROGRESS:
            return { ...state, scrapingProgress: action.payload };
        case ActionTypes.SET_API_AVAILABLE:
            return { ...state, apiAvailable: action.payload };
        default:
            return state;
    }
};

export const AppProvider = ({ children }) => {
    const [state, dispatch] = useReducer(appReducer, initialState);

    const apiActions = useMemo(() => ({
        fetchFacilities: async () => {
            dispatch({ type: ActionTypes.SET_FACILITIES_LOADING, payload: true });
            try {
                const response = await api.facilities.getFacilities();
                dispatch({ type: ActionTypes.SET_FACILITIES, payload: response.facilities || [] });
            } catch (error) {
                console.error("Failed to fetch facilities:", error);
                dispatch({ type: ActionTypes.SET_FACILITIES, payload: [] });
            }
        },
        getFacilityDetails: async (facilityId) => {
            try {
                return await api.facilities.getFacility(facilityId);
            } catch (error) {
                console.error(`Failed to fetch details for ${facilityId}:`, error);
                throw error;
            }
        },
        getAnalysisSummaries: async () => {
            try {
                return await api.results.getAnalysisSummaries();
            } catch (error) {
                console.error('Failed to fetch analysis summaries:', error);
                throw error;
            }
        },
        removeNotification: (notificationId) => {
            dispatch({ type: ActionTypes.REMOVE_NOTIFICATION, payload: notificationId });
        },
        addNotification: (message, type = 'info') => {
            dispatch({
                type: ActionTypes.ADD_NOTIFICATION,
                payload: { message, type }
            });
        },
        sendToKiddie: async (facilityIds, includePricing, maxConcurrent) => {
            try {
                return await api.facilities.sendToKiddie(facilityIds, includePricing, maxConcurrent);
            } catch (error) {
                console.error('Failed to send to Kiddie:', error);
                throw error;
            }
        },
        sendInspectionReportsToKiddie: async (facilityIds, maxConcurrent) => {
            try {
                return await api.facilities.sendInspectionReportsToKiddie(facilityIds, maxConcurrent);
            } catch (error) {
                console.error('Failed to send inspection reports to Kiddie:', error);
                throw error;
            }
        },
        // Add other API actions here as needed...
    }), []);

    useEffect(() => {
        // Only fetch if we're on localhost (development mode)
        // Skip for production deployments to avoid CORS errors
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            apiActions.fetchFacilities();
        }
        // Add other initial fetches if necessary
    }, [apiActions]);

    return (
        <AppContext.Provider value={{ state, apiActions, dispatch }}>
            {children}
        </AppContext.Provider>
    );
};

export const useApp = () => {
    return useContext(AppContext);
}; 